/**
 * Dev Tools ▸ Black Friday — force a point on the 48-hour clock, in THIS browser.
 *
 * Modelled on `credits-widget-sim.ts`, and for the same reason: it substitutes the **data** the
 * feature reads — the free-usage window — rather than the band's own state. So the band, the drawer's
 * plan card, the Settings plan card and the "Free until …" line all repaint together and consistently,
 * instead of a dev switch that moves the band while every other surface still shows the real account.
 *
 * Every accessor is inert without a dev-tools grant (`devToolsUnlocked`), so a key left in storage — or
 * hand-written — does nothing at all in a real user's browser.
 */

import { useEffect, useRef, useState } from 'react';
import { devToolsUnlocked } from './dev-tools';
import { BLACK_FRIDAY_WINDOW_MS } from './black-friday';

/** The bands, named by what the reader would be seeing. */
export type BlackFridaySim = 'running' | 'final' | 'ended-plans' | 'ended-wins';

const SIMS: readonly BlackFridaySim[] = ['running', 'final', 'ended-plans', 'ended-wins'];

const KEY = 'orion-web:dev-black-friday-sim';
const EVENT = 'orion:black-friday-sim';

/** Where in the window each simulated state puts the reader. */
const RUNNING_REMAINING_MS = 24 * 60 * 60 * 1000;
const FINAL_REMAINING_MS = 3 * 60 * 60 * 1000;
const ENDED_AGO_MS = 2 * 60 * 60 * 1000;

export function getBlackFridaySim(): BlackFridaySim | null {
  if (!devToolsUnlocked() || typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(KEY);
    return SIMS.includes(raw as BlackFridaySim) ? (raw as BlackFridaySim) : null;
  } catch {
    return null;
  }
}

export function setBlackFridaySim(sim: BlackFridaySim | null): void {
  if (!devToolsUnlocked() || typeof window === 'undefined') return;
  try {
    if (sim === null) window.localStorage.removeItem(KEY);
    else window.localStorage.setItem(KEY, sim);
  } catch {
    return;
  }
  window.dispatchEvent(new Event(EVENT));
}

/** Live, so flipping the panel repaints every surface without a reload. */
export function useBlackFridaySim(): BlackFridaySim | null {
  const [sim, setSim] = useState<BlackFridaySim | null>(null);
  useEffect(() => {
    const read = () => setSim(getBlackFridaySim());
    read();
    window.addEventListener(EVENT, read);
    window.addEventListener('storage', read);
    return () => {
      window.removeEventListener(EVENT, read);
      window.removeEventListener('storage', read);
    };
  }, []);
  return sim;
}

/**
 * The free-usage window a simulated state implies, or null when it implies none.
 *
 * `useFreeUsageUntil` returns this in place of the account's real window, which is what makes the plan
 * cards follow the band. The two ended states have no OPEN window — `blackFridaySimSeenUntil` supplies
 * the one that ran.
 */
export function blackFridaySimFreeUsageUntil(sim: BlackFridaySim | null, now: number): string | null {
  if (sim === 'running') return new Date(now + RUNNING_REMAINING_MS).toISOString();
  if (sim === 'final') return new Date(now + FINAL_REMAINING_MS).toISOString();
  return null;
}

/**
 * The remembered window end a simulated state implies. The two ended states need one in the past (there
 * is no over-band without a window that ran).
 */
export function blackFridaySimSeenUntil(sim: BlackFridaySim | null, now: number): string | null {
  if (sim === 'ended-plans' || sim === 'ended-wins') return new Date(now - ENDED_AGO_MS).toISOString();
  if (sim === 'running' || sim === 'final') return blackFridaySimFreeUsageUntil(sim, now);
  return null;
}

/** The window a simulated over-band's wins would have landed in — only `ended-wins` claims any. */
export function blackFridaySimHasWins(sim: BlackFridaySim | null): boolean {
  return sim === 'ended-wins';
}

/** Exported for the panel's own labels, and so the test can walk every state. */
export const BLACK_FRIDAY_SIMS = SIMS;
/** Re-exported so the panel can describe the window without importing two modules. */
export { BLACK_FRIDAY_WINDOW_MS };

/** What a simulated state substitutes for the account's own free-usage window. */
export interface BlackFridaySimWindow {
  /** The pick itself, or null when the simulator is off — the one thing callers branch on. */
  sim: BlackFridaySim | null;
  freeUsageUntilIso: string | null;
  seenUntilIso: string | null;
  hasWins: boolean;
}

/**
 * The window a simulated state implies, with its clock ANCHORED to the moment the state was picked.
 *
 * Every consumer must read the simulated window through this hook rather than calling the three pure
 * functions above with `Date.now()` per render. A window end recomputed from the live clock is a NEW
 * ISO string on every render, and the frame's "remember the window this browser saw" effect depends on
 * that string — so each render scheduled the effect, the effect set state, and the new state rendered a
 * new string. Picking `Counting down` crashed the app outright with "Maximum update depth exceeded".
 */
export function useBlackFridaySimWindow(): BlackFridaySimWindow {
  const sim = useBlackFridaySim();
  const [anchor, setAnchor] = useState(() => Date.now());
  const anchored = useRef(sim);
  useEffect(() => {
    if (anchored.current === sim) return;
    anchored.current = sim;
    setAnchor(Date.now());
  }, [sim]);

  return {
    sim,
    freeUsageUntilIso: blackFridaySimFreeUsageUntil(sim, anchor),
    seenUntilIso: blackFridaySimSeenUntil(sim, anchor),
    hasWins: blackFridaySimHasWins(sim),
  };
}
